import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  Utensils, 
  Home, 
  Package, 
  Users, 
  ShoppingCart, 
  BarChart3, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  User,
  CreditCard,
  BookOpen,
  Calendar,
  FileText,
  HelpCircle,
  ChevronDown,
  Monitor,
  Smartphone,
  Download,
  Upload,
  DollarSign,
  TrendingUp,
  UserPlus,
  ShoppingBag,
  Calculator,
  MessageSquare,
  Star,
  AlertCircle,
  UserCheck,
  ExternalLink,
  Sparkles,
  ArrowUpCircle,
  ArrowDownCircle,  FolderOpen,
  Briefcase,
  Wrench,
  Send,
  Cog
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';


const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { person, company, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();  // Navegação principal organizada por categorias
  const navigation = {
    mainItems: [
      { name: 'Dashboard', href: '/dashboard', icon: Home, description: 'Visão geral do sistema' },
      { name: 'PDV', href: '/dashboard/pdv', icon: Monitor, description: 'Ponto de venda', badge: 'Novo' }
    ],    categories: [
      {
        category: 'Cadastros',
        icon: FolderOpen,
        items: [
          { name: 'Produtos', href: '/dashboard/products', icon: Package, description: 'Gerenciar produtos' },
          { name: 'Alunos', href: '/dashboard/students', icon: Users, description: 'Cadastro de alunos' },
          { name: 'Pessoas', href: '/dashboard/people', icon: Users, description: 'Clientes, fornecedores, responsáveis e usuários do sistema' }
        ]
      },      {
        category: 'Financeiro',
        icon: DollarSign,
        items: [
          { name: 'Caixa', href: '/dashboard/cashier', icon: DollarSign, description: 'Controle de caixa' },
          { name: 'Histórico de Vendas', href: '/dashboard/sales', icon: FileText, description: 'Movimentação do PDV' },
          { name: 'Mensalidade', href: '/dashboard/monthly-payments', icon: Calendar, description: 'Controle de alunos mensalistas' },
          { name: 'Relatórios', href: '/dashboard/reports', icon: BarChart3, description: 'Análises e métricas' },
          { name: 'Contas a Receber', href: '/dashboard/accounts-receivable', icon: ArrowUpCircle, description: 'Valores a receber' },
          { name: 'Contas a Pagar', href: '/dashboard/accounts-payable', icon: ArrowDownCircle, description: 'Valores a pagar' },
          { name: 'Extrato do Aluno', href: '/dashboard/student-extract', icon: FileText, description: 'Extrato de movimentações' }
        ]
      },
      {
        category: 'Operacional',
        icon: Briefcase,
        items: [
          { name: 'Cardápio', href: '/dashboard/menu', icon: BookOpen, description: 'Cardápio semanal' },
          { name: 'Estoque', href: '/dashboard/inventory', icon: ShoppingBag, description: 'Controle de estoque' },
          { name: 'Calculadora', href: '/dashboard/calculator', icon: Calculator, description: 'Calculadora de preços' }
        ]
      },
      {
        category: 'Comunicação',
        icon: Send,
        items: [
          { name: 'Mensagens', href: '/dashboard/messages', icon: MessageSquare, description: 'Comunicados aos pais' },
          { name: 'Avaliações', href: '/dashboard/reviews', icon: Star, description: 'Feedback dos usuários' }
        ]
      },
      {
        category: 'Sistema',
        icon: Cog,
        items: [
          { name: 'Configurações', href: '/dashboard/settings', icon: Settings, description: 'Configurações gerais' }
        ]
      }
    ]
  };

  // Notificações mockadas
  const notifications = [
    {
      id: 1,
      type: 'warning',
      title: 'Estoque baixo',
      message: '5 produtos com estoque baixo',
      time: '2 min atrás',
      unread: true
    },
    {
      id: 2,
      type: 'success',
      title: 'Venda realizada',
      message: 'Nova venda de R$ 25,50',
      time: '10 min atrás',
      unread: true
    },
    {
      id: 3,
      type: 'info',
      title: 'Relatório disponível',
      message: 'Relatório mensal gerado',
      time: '1 hora atrás',
      unread: false
    }
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isCurrentPage = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'success':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      default:
        return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 flex z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent navigation={navigation} isCurrentPage={isCurrentPage} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-72 lg:flex-col lg:fixed lg:inset-y-0">
        <SidebarContent navigation={navigation} isCurrentPage={isCurrentPage} />
      </div>

      {/* Main content */}
      <div className="lg:pl-72 flex flex-col flex-1">
        {/* Top header bar */}
        <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              {/* Mobile menu button */}
              <div className="flex items-center lg:hidden">
                <button
                  type="button"
                  className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>

              {/* Page title and breadcrumb */}
              <div className="flex-1 min-w-0 lg:ml-0 ml-4">
                <h1 className="text-2xl font-bold text-gray-900 sm:truncate">
                  {getPageTitle(location.pathname)}
                </h1>
                <div className="mt-1 flex items-center text-sm text-gray-500">
                  <span className="truncate">{company?.name}</span>
                  <span className="mx-2">•</span>
                  <span>Bem-vindo, {person?.name?.split(' ')[0]}</span>
                </div>
              </div>

              {/* Right side actions */}
              <div className="flex items-center space-x-4">
                {/* Search */}
                <div className="hidden md:block">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar..."
                      className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>                {/* Quick actions */}
                <div className="hidden md:flex items-center space-x-2">
                  <Link to="/dashboard/pdv">
                    <Button size="sm" className="text-sm">
                      <Monitor className="w-4 h-4 mr-2" />
                      PDV
                    </Button>
                  </Link>
                  
                  {/* Botão de Suporte melhorado */}
                  <Link to="/dashboard/support">
                    <div className="relative group">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-sm border-orange-200 text-orange-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-800 transition-all duration-200"
                        title="Solicitar Suporte Técnico"
                      >
                        <div className="flex items-center space-x-1">
                          <div className="relative">
                            <HelpCircle className="w-4 h-4" />
                            {/* Pulse indicator para chamar atenção */}
                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                          </div>
                          <span className="hidden lg:inline font-medium">Suporte</span>
                        </div>
                      </Button>
                        {/* Tooltip melhorado */}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <UserCheck className="w-3 h-3" />
                            <span>Solicitar Suporte da CantinaTech</span>
                          </div>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>

                {/* Notifications */}
                <div className="relative">
                  <button
                    type="button"
                    className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full relative"
                    onClick={() => setNotificationsOpen(!notificationsOpen)}
                  >
                    <Bell className="h-6 w-6" />
                    {notifications.filter(n => n.unread).length > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {notifications.filter(n => n.unread).length}
                      </span>
                    )}
                  </button>

                  {/* Notifications dropdown */}
                  {notificationsOpen && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900">Notificações</h3>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 hover:bg-gray-50 ${notification.unread ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mt-1">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="ml-3 flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  {notification.title}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {notification.time}
                                </p>
                              </div>
                              {notification.unread && (
                                <div className="flex-shrink-0">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-gray-200 px-4 py-2">
                        <button className="text-sm text-blue-600 hover:text-blue-500">
                          Ver todas as notificações
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Profile dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center space-x-2 p-2 text-sm rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  >
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <span className="hidden md:block font-medium text-gray-700">
                      {person?.name?.split(' ')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {/* Profile dropdown menu */}
                  {profileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{person?.name}</p>
                        <p className="text-xs text-gray-500">{person?.email}</p>
                      </div>
                      <Link
                        to="/dashboard/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileDropdownOpen(false)}
                      >
                        <User className="w-4 h-4 inline mr-2" />
                        Meu Perfil
                      </Link>
                      <Link
                        to="/dashboard/settings"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileDropdownOpen(false)}
                      >
                        <Settings className="w-4 h-4 inline mr-2" />
                        Configurações
                      </Link>
                      <Link
                        to="/dashboard/support"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileDropdownOpen(false)}
                      >
                        <HelpCircle className="w-4 h-4 inline mr-2" />
                        Suporte
                      </Link>
                      <hr className="my-1" />
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          handleSignOut();
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 inline mr-2" />
                        Sair
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Click outside handlers */}
      {profileDropdownOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setProfileDropdownOpen(false)}
        />
      )}
      {notificationsOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setNotificationsOpen(false)}
        />      )}
    </div>
  );
};

// Componente da Sidebar
const SidebarContent = ({ navigation, isCurrentPage }) => {
  const { company, signOut } = useAuth();
  const navigate = useNavigate();
  // Lê do localStorage o submenu aberto
  const initialCategory = typeof window !== 'undefined' ? localStorage.getItem('sidebarCategory') : null;
  const [expandedCategories, setExpandedCategories] = useState(initialCategory ? [initialCategory] : []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const isOpen = prev.includes(category);
      // Salva no localStorage
      if (!isOpen) localStorage.setItem('sidebarCategory', category);
      else localStorage.removeItem('sidebarCategory');
      return isOpen ? [] : [category];
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
      {/* Logo */}
      <div className="flex-1 flex flex-col pt-3 pb-2 overflow-y-auto">
        <div className="flex items-center flex-shrink-0 px-2 mb-4">
          <div className="gradient-primary p-1.5 rounded-lg">
            <Utensils className="h-5 w-5 text-white" />
          </div>
          <div className="ml-2">
            <h2 className="text-base font-semibold text-gray-900 leading-tight">CantinaTech</h2>
            <p className="text-xs text-gray-500 truncate">{company?.name}</p>
          </div>
        </div>
        {/* Navigation */}
        <nav className="mt-1 flex-1 px-1 space-y-1">
          {/* Main Items - Dashboard e PDV diretos */}
          {navigation.mainItems.map((item) => {
            const Icon = item.icon;
            const current = isCurrentPage(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                  current
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md transform scale-[1.02]'
                    : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:text-blue-700 hover:shadow-sm'
                }`}
              >
                <div className={`p-1.5 rounded-lg mr-2 ${
                  current 
                    ? 'bg-white/20' 
                    : 'bg-gray-100 group-hover:bg-blue-100'
                }`}>
                  <Icon
                    className={`h-4 w-4 ${
                      current ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold truncate">{item.name}</span>
                    {item.badge && (
                      <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                        current
                          ? 'bg-white/20 text-white'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className={`text-[10px] mt-0.5 truncate ${
                      current ? 'text-blue-100' : 'text-gray-500 group-hover:text-blue-600'
                    }`}>
                      {item.description}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}

          {/* Separador visual */}
          <div className="border-t border-gray-200 my-2"></div>
          {/* Categories with submenus */}
          {navigation.categories.map((section) => {
            const CategoryIcon = section.icon;
            return (
              <div key={section.category}>
                <button
                  onClick={() => toggleCategory(section.category)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold rounded-lg transition-all duration-300 group relative overflow-hidden ${
                    expandedCategories.includes(section.category)
                      ? 'bg-gradient-to-r from-blue-500 via-blue-600 to-purple-600 text-white shadow-xl shadow-blue-500/30 transform scale-[1.02]'
                      : 'bg-gradient-to-r from-gray-50 to-white text-gray-700 hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 border border-gray-200 hover:border-blue-200 hover:shadow-lg'
                  }`}
                >
                  <div className={`flex items-center space-x-2 relative z-10`}>
                    <div className={`p-1.5 rounded-lg transition-all duration-300 ${
                      expandedCategories.includes(section.category)
                        ? 'bg-white/20 backdrop-blur-sm'
                        : 'bg-blue-100 group-hover:bg-blue-200'
                    }`}>
                      <CategoryIcon
                        className={`w-4 h-4 transition-colors duration-300 ${
                          expandedCategories.includes(section.category)
                            ? 'text-white'
                            : 'text-blue-600 group-hover:text-blue-700'
                        }`}
                      />
                    </div>
                    <span className="font-semibold tracking-wide truncate">{section.category}</span>
                    {expandedCategories.includes(section.category) && (
                      <Sparkles className="w-3 h-3 text-white/60 animate-pulse" />
                    )}
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 transition-all duration-300 relative z-10 ${
                      expandedCategories.includes(section.category) 
                        ? 'rotate-180 text-white' 
                        : 'text-gray-500 group-hover:text-blue-600'
                    }`} 
                  />
                </button>
                {expandedCategories.includes(section.category) && (
                  <div className="mt-2 space-y-1 pl-2">
                    <div className="border-l-2 border-blue-200 pl-2 space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const current = isCurrentPage(item.href);
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center px-2 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                              current
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md transform scale-[1.02] border-l-4 border-blue-300'
                                : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:text-blue-700 hover:shadow-sm'
                            }`}
                          >
                            <div className={`p-1 rounded-lg mr-2 ${
                              current 
                                ? 'bg-white/20' 
                                : 'bg-gray-100 group-hover:bg-blue-100'
                            }`}>
                              <Icon
                                className={`h-3.5 w-3.5 ${
                                  current ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'
                                }`}
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium truncate">{item.name}</span>
                                {item.badge && (
                                  <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                                    current
                                      ? 'bg-white/20 text-white'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {item.badge}
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <p className={`text-[10px] mt-0.5 truncate ${
                                  current ? 'text-blue-100' : 'text-gray-500 group-hover:text-blue-600'
                                }`}>
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
      {/* Bottom section */}
      <div className="flex-shrink-0 p-2 border-t border-gray-200">
        {/* App Mobile Banner - Mais destacado */}
        <div className="relative mb-2 p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white overflow-hidden">
          <div className="absolute top-0 right-0 opacity-20">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <div className="relative flex items-center space-x-2">
            <div className="relative">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 p-0.5 bg-green-500 rounded-full">
                <UserCheck className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-1">
                <p className="text-xs font-bold text-white">📱 App Mobile</p>
                <span className="px-1.5 py-0.5 bg-green-400 text-green-900 text-[10px] font-semibold rounded-full">
                  NOVO
                </span>
              </div>
              <p className="text-[10px] text-blue-100 mt-0.5">
                Pais acompanham gastos dos filhos
              </p>
            </div>
            <button className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors backdrop-blur-sm">
              <ExternalLink className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <p className="text-[10px] text-blue-100 mb-1">Disponível para download:</p>
            <div className="flex space-x-1">
              <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] font-medium">Play Store</span>
              <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] font-medium">App Store</span>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 text-xs py-2"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sair do Sistema
        </Button>
      </div>
    </div>
  );
};

// Função para obter o título da página com base na rota
const getPageTitle = (pathname) => {
  const routes = {
    '/dashboard': 'Dashboard',
    '/dashboard/pdv': 'Ponto de Venda (PDV)',
    '/dashboard/pdv/dashboard': 'Dashboard PDV',
    '/dashboard/pdv/reports': 'Relatórios PDV',
    '/dashboard/sales': 'Histórico de Vendas',    '/dashboard/products': 'Produtos',
    '/dashboard/products/new': 'Cadastrar Produto',    '/dashboard/students': 'Alunos',
    '/dashboard/students/new': 'Cadastrar Aluno',
    '/dashboard/users': 'Usuários',
    '/dashboard/users/new': 'Cadastrar Usuário',
    '/dashboard/reports': 'Relatórios Financeiros',
    '/dashboard/settings': 'Configurações',
    '/dashboard/cashier': 'Controle de Caixa',
    '/dashboard/monthly-payments': 'Controle de Mensalidade',
    '/dashboard/menu': 'Cardápio',
    '/dashboard/inventory': 'Estoque',
    '/dashboard/messages': 'Mensagens',
    '/dashboard/support': 'Suporte',
    '/dashboard/profile': 'Meu Perfil',
    '/dashboard/backup': 'Backup',
    '/dashboard/import': 'Importação',
    '/dashboard/calculator': 'Calculadora',
    '/dashboard/accounts-receivable': 'Contas a Receber',
    '/dashboard/accounts-payable': 'Contas a Pagar',
    '/dashboard/reviews': 'Avaliações'
  };

  // Handle dynamic routes with parameters
  if (pathname.includes('/dashboard/products/') && pathname.includes('/edit')) {
    return 'Editar Produto';
  }
  if (pathname.includes('/dashboard/products/') && !pathname.includes('/edit') && !pathname.includes('/new')) {
    return 'Detalhes do Produto';
  }
  if (pathname.includes('/dashboard/students/') && pathname.includes('/edit')) {
    return 'Editar Aluno';  }
  if (pathname.includes('/dashboard/students/') && !pathname.includes('/edit') && !pathname.includes('/new')) {
    return 'Detalhes do Aluno';
  }

  return routes[pathname] || 'CantinaTech';
};

export default DashboardLayout;